import * as React from "react";
import { Navigate } from "react-router";
import * as Dtos from "./dtos/Tmd.dtos";
import { AppRouteObject } from "./hooks/useChildRouteData";
import BaseLayout from "./components/layouts/BaseLayout";
import { OverlayLoading } from "./components/common";
import { UpdateLocation } from "./actions/routing";
import useDispatch from "./hooks/useDispatch";
import useSelector from "./hooks/useSelector";
import { useLocation } from "react-router";
import RouteErrorBoundary from "./components/RouteErrorBoundary";

// Lazy-loaded components
const CommitteeMeetingAttendancePage = React.lazy(() => import("./containers/Committees/CommitteeMeetingAttendance/CommitteeMeetingAttendancePage").then(m => ({ default: m.default })));
const CommitteeMeetingAttendancesPage = React.lazy(() => import("./containers/Committees/CommitteeMeetingAttendances/CommitteeMeetingAttendancesPage").then(m => ({ default: m.default })));
const CommitteeMeetingPage = React.lazy(() => import("./containers/Committees/CommitteeMeeting/CommitteeMeetingPage").then(m => ({ default: m.default })));
const CommitteeMeetingsPage = React.lazy(() => import("./containers/Committees/CommitteeMeetings/CommitteeMeetingsPage").then(m => ({ default: m.default })));
const CommitteeMemberPage = React.lazy(() => import("./containers/Committees/CommitteeMember/CommitteeMemberPage").then(m => ({ default: m.default })));
const CommitteeMemberRolePage = React.lazy(() => import("./containers/Committees/CommitteeMemberRole/CommitteeMemberRolePage").then(m => ({ default: m.default })));
const CommitteeMemberRolesPage = React.lazy(() => import("./containers/Committees/CommitteeMemberRoles/CommitteeMemberRolesPage").then(m => ({ default: m.default })));
const CommitteeMembersPage = React.lazy(() => import("./containers/Committees/CommitteeMembers/CommitteeMembersPage").then(m => ({ default: m.default })));
const CommitteePage = React.lazy(() => import("./containers/Committees/Committee/CommitteePage").then(m => ({ default: m.default })));
const CommitteeRegularInviteePage = React.lazy(() => import("./containers/Committees/CommitteeRegularInvitee/CommitteeRegularInviteePage").then(m => ({ default: m.default })));
const CommitteeRegularInviteesPage = React.lazy(() => import("./containers/Committees/CommitteeRegularInvitees/CommitteeRegularInviteesPage").then(m => ({ default: m.default })));
const CommitteesPage = React.lazy(() => import("./containers/Committees/Committees/CommitteesPage").then(m => ({ default: m.default })));
const CountriesPage = React.lazy(() => import("./containers/Lookups/Countries/CountriesPage").then(m => ({ default: m.default })));
const CountryPage = React.lazy(() => import("./containers/Lookups/Country/CountryPage").then(m => ({ default: m.default })));
const DashboardPage = React.lazy(() => import("./containers/Dashboard/DashboardPage").then(m => ({ default: m.default })));
const DiscussionPage = React.lazy(() => import("./containers/Projects/Discussion/DiscussionPage").then(m => ({ default: m.default })));
const DiscussionsPage = React.lazy(() => import("./containers/Projects/Discussions/DiscussionsPage").then(m => ({ default: m.default })));
const ErrorPage = React.lazy(() => import("./containers/Error/errorPage").then(m => ({ default: m.default })));
const FacilityAllianceMemberPage = React.lazy(() => import("./containers/InstitutionLookups/FacilityAllianceMember/FacilityAllianceMemberPage").then(m => ({ default: m.default })));
const FacilityAllianceMembersPage = React.lazy(() => import("./containers/InstitutionLookups/FacilityAllianceMembers/FacilityAllianceMembersPage").then(m => ({ default: m.default })));
const FellowshipPage = React.lazy(() => import("./containers/Personnel/Fellowship/FellowshipPage").then(m => ({ default: m.default })));
const FellowshipsPage = React.lazy(() => import("./containers/Personnel/Fellowship/FellowshipsPage").then(m => ({ default: m.default })));
const FormTemplateFileTypePage = React.lazy(() => import("./containers/QALookups/FormTemplateFileType/FormTemplateFileTypePage").then(m => ({ default: m.default })));
const FormTemplateFileTypesPage = React.lazy(() => import("./containers/QALookups/FormTemplateFileTypes/FormTemplateFileTypesPage").then(m => ({ default: m.default })));
const FormTemplatePage = React.lazy(() => import("./containers/QALookups/FormTemplate/FormTemplatePage").then(m => ({ default: m.default })));
const FormTemplatesPage = React.lazy(() => import("./containers/QALookups/FormTemplates/FormTemplatesPage").then(m => ({ default: m.default })));
const GeneralSettingsPage = React.lazy(() => import("./containers/Settings/GeneralSettings/GeneralSettingsPage").then(m => ({ default: m.default })));
const GroupContractPage = React.lazy(() => import("./containers/Groups/GroupContract/GroupContractPage").then(m => ({ default: m.default })));
const GroupFinancePage = React.lazy(() => import("./containers/Groups/GroupFinance/GroupFinancePage").then(m => ({ default: m.default })));
const GroupItemTemplateCategoryPage = React.lazy(() => import("./containers/GroupSettings/GroupItemTemplateCategory/GroupItemTemplateCategoryPage").then(m => ({ default: m.default })));
const GroupItemTemplateCategorysPage = React.lazy(() => import("./containers/GroupSettings/GroupItemTemplateCategorys/GroupItemTemplateCategorysPage").then(m => ({ default: m.default })));
const GroupItemTemplatePage = React.lazy(() => import("./containers/GroupSettings/GroupItemTemplate/GroupItemTemplatePage").then(m => ({ default: m.default })));
const GroupItemTemplatesPage = React.lazy(() => import("./containers/GroupSettings/GroupItemTemplates/GroupItemTemplatesPage").then(m => ({ default: m.default })));
const GroupItemsPage = React.lazy(() => import("./containers/Groups/GroupItems/GroupItemsPage").then(m => ({ default: m.default })));
const GroupNotesPage = React.lazy(() => import("./containers/Groups/GroupNotes/GroupNotesPage").then(m => ({ default: m.default })));
const GroupPage = React.lazy(() => import("./containers/Groups/Group/GroupPage").then(m => ({ default: m.default })));
const GroupPersonnelPage = React.lazy(() => import("./containers/Groups/GroupPersonnel/GroupPersonnelPage").then(m => ({ default: m.default })));
const GroupPersonnelsPage = React.lazy(() => import("./containers/Groups/GroupPersonnels/GroupPersonnelsPage").then(m => ({ default: m.default })));
const GroupSettingsPage = React.lazy(() => import("./containers/Settings/GroupSettings/GroupSettingsPage").then(m => ({ default: m.default })));
const GroupTrialsPage = React.lazy(() => import("./containers/Groups/GroupTrials/GroupTrialsPage").then(m => ({ default: m.default })));
const GroupsPage = React.lazy(() => import("./containers/Groups/Groups/GroupsPage").then(m => ({ default: m.default })));
const HealthDistrictPage = React.lazy(() => import("./containers/Settings/HealthDistrict/HealthDistrictPage").then(m => ({ default: m.default })));
const HealthDistrictsPage = React.lazy(() => import("./containers/Settings/HealthDistrict/HealthDistrictsPage").then(m => ({ default: m.default })));
const HrecPage = React.lazy(() => import("./containers/Settings/HREC/HrecPage").then(m => ({ default: m.default })));
const HrecsPage = React.lazy(() => import("./containers/Settings/HREC/HrecsPage").then(m => ({ default: m.default })));
const InstitutionCodePage = React.lazy(() => import("./containers/Institutions/InstitutionCode/InstitutionCodePage").then(m => ({ default: m.default })));
const InstitutionCodesPage = React.lazy(() => import("./containers/Institutions/InstitutionCodes/InstitutionCodesPage").then(m => ({ default: m.default })));
const InstitutionCodingPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionCoding/InstitutionCodingPage").then(m => ({ default: m.default })));
const InstitutionCodingsPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionCodings/InstitutionCodingsPage").then(m => ({ default: m.default })));
const InstitutionContractPage = React.lazy(() => import("./containers/Institutions/InstitutionContract/InstitutionContractPage").then(m => ({ default: m.default })));
const InstitutionDepartmentContactEditPage = React.lazy(() => import("./containers/Institutions/InstitutionDepartmentContactEdit/InstitutionDepartmentContactEditPage").then(m => ({ default: m.default })));
const InstitutionDepartmentEditPage = React.lazy(() => import("./containers/Institutions/InstitutionDepartmentEdit/InstitutionDepartmentEditPage").then(m => ({ default: m.default })));
const InstitutionDepartmentPersonnelEditPage = React.lazy(() => import("./containers/Institutions/InstitutionDepartmentPersonnelEdit/InstitutionDepartmentPersonnelEditPage").then(m => ({ default: m.default })));
const InstitutionDepartmentsPage = React.lazy(() => import("./containers/Institutions/InstitutionDepartments/InstitutionDepartmentsPage").then(m => ({ default: m.default })));
const InstitutionFinancePage = React.lazy(() => import("./containers/Institutions/InstitutionFinance/InstitutionFinancePage").then(m => ({ default: m.default })));
const InstitutionItemTemplateCategoriesPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionItemTemplateCategories/InstitutionItemTemplateCategoriesPage").then(m => ({ default: m.default })));
const InstitutionItemTemplateCategoryPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionItemTemplateCategory/InstitutionItemTemplateCategoryPage").then(m => ({ default: m.default })));
const InstitutionItemTemplatePage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionItemTemplate/InstitutionItemTemplatePage").then(m => ({ default: m.default })));
const InstitutionItemTemplatesPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionItemTemplates/InstitutionItemTemplatesPage").then(m => ({ default: m.default })));
const InstitutionItemsPage = React.lazy(() => import("./containers/Institutions/InstitutionItems/InstitutionItemsPage").then(m => ({ default: m.default })));
const InstitutionLaboratoriesPage = React.lazy(() => import("./containers/Institutions/InstitutionLaboratories/InstitutionLaboratoriesPage").then(m => ({ default: m.default })));
const InstitutionLaboratoryPage = React.lazy(() => import("./containers/Institutions/InstitutionLaboratory/InstitutionLaboratoryPage").then(m => ({ default: m.default })));
const InstitutionNotesPage = React.lazy(() => import("./containers/Institutions/InstitutionNotes/InstitutionNotesPage").then(m => ({ default: m.default })));
const InstitutionPage = React.lazy(() => import("./containers/Institutions/Institution/InstitutionPage").then(m => ({ default: m.default })));
const InstitutionPersonnelPage = React.lazy(() => import("./containers/Institutions/InstitutionPersonnel/InstitutionPersonnelPage").then(m => ({ default: m.default })));
const InstitutionPersonnelsPage = React.lazy(() => import("./containers/Institutions/InstitutionPersonnels/InstitutionPersonnelsPage").then(m => ({ default: m.default })));
const InstitutionProcedurePage = React.lazy(() => import("./containers/Institutions/InstitutionProcedure/InstitutionProcedurePage").then(m => ({ default: m.default })));
const InstitutionProceduresPage = React.lazy(() => import("./containers/Institutions/InstitutionProcedures/InstitutionProceduresPage").then(m => ({ default: m.default })));
const InstitutionSettingsPage = React.lazy(() => import("./containers/Settings/InstitutionSettings/InstitutionSettingsPage").then(m => ({ default: m.default })));
const InstitutionTrialsPage = React.lazy(() => import("./containers/Institutions/InstitutionTrials/InstitutionTrialsPage").then(m => ({ default: m.default })));
const InstitutionTypePage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionType/InstitutionTypePage").then(m => ({ default: m.default })));
const InstitutionTypesPage = React.lazy(() => import("./containers/InstitutionLookups/InstitutionTypes/InstitutionTypesPage").then(m => ({ default: m.default })));
const InstitutionsPage = React.lazy(() => import("./containers/Institutions/Institutions/InstitutionsPage").then(m => ({ default: m.default })));
const InterestPage = React.lazy(() => import("./containers/Lookups/Interest/InterestPage").then(m => ({ default: m.default })));
const InterestsPage = React.lazy(() => import("./containers/Lookups/Interests/InterestsPage").then(m => ({ default: m.default })));
const ItemComponentPage = React.lazy(() => import("./containers/Settings/ItemComponent/ItemComponentPage").then(m => ({ default: m.default })));
const ItemComponentTypePage = React.lazy(() => import("./containers/Lookups/ItemComponentType/ItemComponentTypePage").then(m => ({ default: m.default })));
const ItemComponentTypesPage = React.lazy(() => import("./containers/Lookups/ItemComponentTypes/ItemComponentTypesPage").then(m => ({ default: m.default })));
const ItemComponentsPage = React.lazy(() => import("./containers/Settings/ItemComponents/ItemComponentsPage").then(m => ({ default: m.default })));
const KeyContactPage = React.lazy(() => import("./containers/InstitutionLookups/KeyContact/KeyContactPage").then(m => ({ default: m.default })));
const KeyContactsPage = React.lazy(() => import("./containers/InstitutionLookups/KeyContacts/KeyContactsPage").then(m => ({ default: m.default })));
const LabContractPage = React.lazy(() => import("./containers/Labs/LabContract/LabContractPage").then(m => ({ default: m.default })));
const LabDepartmentPage = React.lazy(() => import("./containers/Labs/LabDepartment/LabDepartmentPage").then(m => ({ default: m.default })));
const LabDepartmentsPage = React.lazy(() => import("./containers/Labs/LabDepartments/LabDepartmentsPage").then(m => ({ default: m.default })));
const LabFinancePage = React.lazy(() => import("./containers/Labs/LabFinance/LabFinancePage").then(m => ({ default: m.default })));
const LabInstitutionPage = React.lazy(() => import("./containers/Labs/LabInstitution/LabInstitutionPage").then(m => ({ default: m.default })));
const LabInstitutionsPage = React.lazy(() => import("./containers/Labs/LabInstitutions/LabInstitutionsPage").then(m => ({ default: m.default })));
const LabPage = React.lazy(() => import("./containers/Labs/Lab/LabPage").then(m => ({ default: m.default })));
const LabPersonnelPage = React.lazy(() => import("./containers/Labs/LabPersonnel/LabPersonnelPage").then(m => ({ default: m.default })));
const LabPersonnelsPage = React.lazy(() => import("./containers/Labs/LabPersonnels/LabPersonnelsPage").then(m => ({ default: m.default })));
const LabsPage = React.lazy(() => import("./containers/Labs/Labs/LabsPage").then(m => ({ default: m.default })));
const MembershipTypePage = React.lazy(() => import("./containers/Lookups/MembershipType/MembershipTypePage").then(m => ({ default: m.default })));
const MembershipTypesPage = React.lazy(() => import("./containers/Lookups/MembershipTypes/MembershipTypesPage").then(m => ({ default: m.default })));
const MonitoringDocumentPage = React.lazy(() => import("./containers/Monitoring/MonitoringDocument/MonitoringDocumentPage").then(m => ({ default: m.default })));
const MonitoringDocumentsPage = React.lazy(() => import("./containers/Monitoring/MonitoringDocuments/MonitoringDocumentsPage").then(m => ({ default: m.default })));
const MonitoringIssueDocumentPage = React.lazy(() => import("./containers/Monitoring/MonitoringIssueDocument/MonitoringIssueDocumentPage").then(m => ({ default: m.default })));
const MonitoringIssueDocumentsPage = React.lazy(() => import("./containers/Monitoring/MonitoringIssueDocuments/MonitoringIssueDocumentsPage").then(m => ({ default: m.default })));
const MonitoringIssuePage = React.lazy(() => import("./containers/Monitoring/MonitoringIssue/MonitoringIssuePage").then(m => ({ default: m.default })));
const MonitoringIssuesPage = React.lazy(() => import("./containers/Monitoring/MonitoringIssues/MonitoringIssuesPage").then(m => ({ default: m.default })));
const MonitoringPage = React.lazy(() => import("./containers/Monitoring/Monitoring/MonitoringPage").then(m => ({ default: m.default })));
const MonitoringsPage = React.lazy(() => import("./containers/Monitoring/Monitorings/MonitoringsPage").then(m => ({ default: m.default })));
const MyTrialsPage = React.lazy(() => import("./containers/MyTrials/MyTrials/MyTrialsPage").then(m => ({ default: m.default })));
const NotePage = React.lazy(() => import("./containers/Notes/Note/NotePage").then(m => ({ default: m.default })));
const NoteTypePage = React.lazy(() => import("./containers/Lookups/NoteType/NoteTypePage").then(m => ({ default: m.default })));
const NoteTypesPage = React.lazy(() => import("./containers/Lookups/NoteTypes/NoteTypesPage").then(m => ({ default: m.default })));
const OpenClinicaUserRolesPage = React.lazy(() => import("./containers/Settings/OpenClinicaUserRoles/OpenClinicaUserRolesPage").then(m => ({ default: m.default })));
const PersonnelActiveDirectoryPage = React.lazy(() => import("./containers/Personnel/PersonnelActiveDirectory/PersonnelActiveDirectoryPage").then(m => ({ default: m.default })));
const PersonnelByRolesPage = React.lazy(() => import("./containers/Personnel/PersonnelByRoles/PersonnelByRolesPage").then(m => ({ default: m.default })));
const PersonnelNotesPage = React.lazy(() => import("./containers/Personnel/PersonnelNotes/PersonnelNotesPage").then(m => ({ default: m.default })));
const PersonnelOCRolesPage = React.lazy(() => import("./containers/Personnel/PersonnelOCRoles/PersonnelOCRolesPage").then(m => ({ default: m.default })));
const PersonnelPage = React.lazy(() => import("./containers/Personnel/Personnel/PersonnelPage").then(m => ({ default: m.default })));
const PersonnelPermissionsPage = React.lazy(() => import("./containers/Personnel/PersonnelPermissions/PersonnelPermissionsPage").then(m => ({ default: m.default })));
const PersonnelRolePage = React.lazy(() => import("./containers/Personnel/PersonnelRole/PersonnelRolePage").then(m => ({ default: m.default })));
const PersonnelRolesPage = React.lazy(() => import("./containers/Personnel/PersonnelRoles/PersonnelRolesPage").then(m => ({ default: m.default })));
const PersonnelTrainingCertificationPage = React.lazy(() => import("./containers/Personnel/PersonnelTrainingCertification/PersonnelTrainingCertificationPage").then(m => ({ default: m.default })));
const PersonnelsPage = React.lazy(() => import("./containers/Personnel/Personnels/PersonnelsPage").then(m => ({ default: m.default })));
const ProfessionalDisciplinePage = React.lazy(() => import("./containers/Lookups/ProfessionalDiscipline/ProfessionalDisciplinePage").then(m => ({ default: m.default })));
const ProfessionalDisciplinesPage = React.lazy(() => import("./containers/Lookups/ProfessionalDisciplines/ProfessionalDisciplinesPage").then(m => ({ default: m.default })));
const ProjectInvestigatorPage = React.lazy(() => import("./containers/Projects/ProjectInvestigator/ProjectInvestigatorPage").then(m => ({ default: m.default })));
const ProjectItemImportPage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemImport/ProjectItemImportPage").then(m => ({ default: m.default })));
const ProjectItemTemplateCategoryPage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemTemplateCategory/ProjectItemTemplateCategoryPage").then(m => ({ default: m.default })));
const ProjectItemTemplateCategorysPage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemTemplateCategorys/ProjectItemTemplateCategorysPage").then(m => ({ default: m.default })));
const ProjectItemTemplatePage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemTemplate/ProjectItemTemplatePage").then(m => ({ default: m.default })));
const ProjectItemTemplatesPage = React.lazy(() => import("./containers/ProjectLookups/ProjectItemTemplates/ProjectItemTemplatesPage").then(m => ({ default: m.default })));
const ProjectItemsPage = React.lazy(() => import("./containers/Projects/ProjectItems/ProjectItemsPage").then(m => ({ default: m.default })));
const ProjectPage = React.lazy(() => import("./containers/Projects/Project/ProjectPage").then(m => ({ default: m.default })));
const ProjectSettingsPage = React.lazy(() => import("./containers/Settings/ProjectSettings/ProjectSettingsPage").then(m => ({ default: m.default })));
const ProjectsPage = React.lazy(() => import("./containers/Projects/Projects/ProjectsPage").then(m => ({ default: m.default })));
const QAEmailTemplatePage = React.lazy(() => import("./containers/QALookups/QAEmailTemplate/QAEmailTemplatePage").then(m => ({ default: m.default })));
const QAEmailTemplatesPage = React.lazy(() => import("./containers/QALookups/QAEmailTemplates/QAEmailTemplatesPage").then(m => ({ default: m.default })));
const QaSettingsPage = React.lazy(() => import("./containers/Settings/QaSettings/QaSettingsPage").then(m => ({ default: m.default })));
const ReportingTagCategoriesPage = React.lazy(() => import("./containers/QALookups/ReportingTagCategories/ReportingTagCategoriesPage").then(m => ({ default: m.default })));
const ReportingTagCategoryPage = React.lazy(() => import("./containers/QALookups/ReportingTagCategory/ReportingTagCategoryPage").then(m => ({ default: m.default })));
const ReportingTagPage = React.lazy(() => import("./containers/QALookups/ReportingTag/ReportingTagPage").then(m => ({ default: m.default })));
const ReportingTagsPage = React.lazy(() => import("./containers/QALookups/ReportingTags/ReportingTagsPage").then(m => ({ default: m.default })));
const RolePage = React.lazy(() => import("./containers/Lookups/Role/RolePage").then(m => ({ default: m.default })));
const RolesPage = React.lazy(() => import("./containers/Lookups/Roles/RolesPage").then(m => ({ default: m.default })));
const SalutationPage = React.lazy(() => import("./containers/Lookups/Salutation/SalutationPage").then(m => ({ default: m.default })));
const SalutationsPage = React.lazy(() => import("./containers/Lookups/Salutations/SalutationsPage").then(m => ({ default: m.default })));
const SearchPage = React.lazy(() => import("./containers/Search/SearchPage").then(m => ({ default: m.default })));
const SettingsPage = React.lazy(() => import("./containers/Settings/SettingsPage").then(m => ({ default: m.default })));
const SiteLocationPage = React.lazy(() => import("./containers/InstitutionLookups/SiteLocation/SiteLocationPage").then(m => ({ default: m.default })));
const SiteLocationsPage = React.lazy(() => import("./containers/InstitutionLookups/SiteLocations/SiteLocationsPage").then(m => ({ default: m.default })));
const StatePage = React.lazy(() => import("./containers/Lookups/State/StatePage").then(m => ({ default: m.default })));
const StatesPage = React.lazy(() => import("./containers/Lookups/States/StatesPage").then(m => ({ default: m.default })));
const TaskTypePage = React.lazy(() => import("./containers/QALookups/TaskType/TaskTypePage").then(m => ({ default: m.default })));
const TaskTypesPage = React.lazy(() => import("./containers/QALookups/TaskTypes/TaskTypesPage").then(m => ({ default: m.default })));
const TrackerMilestonePage = React.lazy(() => import("./containers/QALookups/TrackerMilestone/TrackerMilestonePage").then(m => ({ default: m.default })));
const TrackerMilestonesPage = React.lazy(() => import("./containers/QALookups/TrackerMilestones/TrackerMilestonesPage").then(m => ({ default: m.default })));
const TrackerTypePage = React.lazy(() => import("./containers/QALookups/TrackerType/TrackerTypePage").then(m => ({ default: m.default })));
const TrackerTypesPage = React.lazy(() => import("./containers/QALookups/TrackerTypes/TrackerTypesPage").then(m => ({ default: m.default })));
const TrialAccrualsPage = React.lazy(() => import("./containers/Trials/TrialAccruals/TrialAccrualsPage").then(m => ({ default: m.default })));
const TrialDoiPage = React.lazy(() => import("./containers/Trials/TrialDoi/TrialDoiPage").then(m => ({ default: m.default })));
const TrialFinancePage = React.lazy(() => import("./containers/Trials/TrialFinance/TrialFinancePage").then(m => ({ default: m.default })));
const TrialGroupContractPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupContract/TrialGroupContractPage").then(m => ({ default: m.default })));
const TrialGroupContractsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupContracts/TrialGroupContractsPage").then(m => ({ default: m.default })));
const TrialGroupFinancePage = React.lazy(() => import("./containers/TrialGroup/TrialGroupFinance/TrialGroupFinancePage").then(m => ({ default: m.default })));
const TrialGroupItemTemplateCategoriesPage = React.lazy(() => import("./containers/TrialGroupLookups/TrialGroupItemTemplateCategories/TrialGroupItemTemplateCategoriesPage").then(m => ({ default: m.default })));
const TrialGroupItemTemplateCategoryPage = React.lazy(() => import("./containers/TrialGroupLookups/TrialGroupItemTemplateCategory/TrialGroupItemTemplateCategoryPage").then(m => ({ default: m.default })));
const TrialGroupItemTemplatePage = React.lazy(() => import("./containers/TrialGroupLookups/TrialGroupItemTemplate/TrialGroupItemTemplatePage").then(m => ({ default: m.default })));
const TrialGroupItemTemplatesPage = React.lazy(() => import("./containers/TrialGroupLookups/TrialGroupItemTemplates/TrialGroupItemTemplatesPage").then(m => ({ default: m.default })));
const TrialGroupItemsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupItems/TrialGroupItemsPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoriesPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratories/TrialGroupLaboratoriesPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryContractPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryContract/TrialGroupLaboratoryContractPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryContractsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryContracts/TrialGroupLaboratoryContractsPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryFinancePage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryFinance/TrialGroupLaboratoryFinancePage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratory/TrialGroupLaboratoryPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryPersonnelPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryPersonnel/TrialGroupLaboratoryPersonnelPage").then(m => ({ default: m.default })));
const TrialGroupLaboratoryPersonnelsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupLaboratoryPersonnels/TrialGroupLaboratoryPersonnelsPage").then(m => ({ default: m.default })));
const TrialGroupPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupPage/TrialGroupPage").then(m => ({ default: m.default })));
const TrialGroupPersonnelPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupPersonnel/TrialGroupPersonnelPage").then(m => ({ default: m.default })));
const TrialGroupPersonnelsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroupPersonnels/TrialGroupPersonnelsPage").then(m => ({ default: m.default })));
const TrialGroupSettingsPage = React.lazy(() => import("./containers/Settings/TrialGroupSettings/TrialGroupSettingsPage").then(m => ({ default: m.default })));
const TrialGroupsPage = React.lazy(() => import("./containers/TrialGroup/TrialGroups/TrialGroupsPage").then(m => ({ default: m.default })));
const TrialInstitutionContractPage = React.lazy(() => import("./containers/Trials/TrialInstitutionContract/TrialInstitutionContractPage").then(m => ({ default: m.default })));
const TrialInstitutionContractsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionContracts/TrialInstitutionContractsPage").then(m => ({ default: m.default })));
const TrialInstitutionFinancePage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionFinance/TrialInstitutionFinancePage").then(m => ({ default: m.default })));
const TrialInstitutionItemCustomViewDataEntryPage = React.lazy(() => import("./containers/Trials/TrialInstitutionItemCustomViewDataEntry/TrialInstitutionItemCustomViewDataEntryPage").then(m => ({ default: m.default })));
const TrialInstitutionItemCustomViewPage = React.lazy(() => import("./containers/Trials/TrialInstitutionItemCustomView/TrialInstitutionItemCustomViewPage").then(m => ({ default: m.default })));
const TrialInstitutionItemCustomViewsPage = React.lazy(() => import("./containers/Trials/TrialInstitutionItemCustomViews/TrialInstitutionItemCustomViewsPage").then(m => ({ default: m.default })));
const TrialInstitutionItemTemplateCategoriesPage = React.lazy(() => import("./containers/TrialInstitutionLookups/TrialInstitutionItemTemplateCategories/TrialInstitutionItemTemplateCategoriesPage").then(m => ({ default: m.default })));
const TrialInstitutionItemTemplateCategoryPage = React.lazy(() => import("./containers/TrialInstitutionLookups/TrialInstitutionItemTemplateCategory/TrialInstitutionItemTemplateCategoryPage").then(m => ({ default: m.default })));
const TrialInstitutionItemTemplatePage = React.lazy(() => import("./containers/TrialInstitutionLookups/TrialInstitutionItemTemplate/TrialInstitutionItemTemplatePage").then(m => ({ default: m.default })));
const TrialInstitutionItemTemplatesPage = React.lazy(() => import("./containers/TrialInstitutionLookups/TrialInstitutionItemTemplates/TrialInstitutionItemTemplatesPage").then(m => ({ default: m.default })));
const TrialInstitutionItemsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionItems/TrialInstitutionItemsPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoriesPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratories/TrialInstitutionLaboratoriesPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryContractPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryContract/TrialInstitutionLaboratoryContractPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryContractsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryContracts/TrialInstitutionLaboratoryContractsPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryFinancePage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryFinance/TrialInstitutionLaboratoryFinancePage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratory/TrialInstitutionLaboratoryPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryPersonnelPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryPersonnel/TrialInstitutionLaboratoryPersonnelPage").then(m => ({ default: m.default })));
const TrialInstitutionLaboratoryPersonnelsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionLaboratoryPersonnels/TrialInstitutionLaboratoryPersonnelsPage").then(m => ({ default: m.default })));
const TrialInstitutionNotesPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionNotes/TrialInstitutionNotesPage").then(m => ({ default: m.default })));
const TrialInstitutionPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitution/TrialInstitutionPage").then(m => ({ default: m.default })));
const TrialInstitutionPersonnelPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionPersonnel/TrialInstitutionPersonnelPage").then(m => ({ default: m.default })));
const TrialInstitutionPersonnelsPage = React.lazy(() => import("./containers/TrialInstitution/TrialInstitutionPersonnels/TrialInstitutionPersonnelsPage").then(m => ({ default: m.default })));
const TrialInstitutionSettingsPage = React.lazy(() => import("./containers/Settings/TrialInstitutionSettings/TrialInstitutionSettingsPage").then(m => ({ default: m.default })));
const TrialInstitutionsPage = React.lazy(() => import("./containers/Trials/TrialInstitutions/TrialInstitutionsPage").then(m => ({ default: m.default })));
const TrialItemTemplateCategoriesPage = React.lazy(() => import("./containers/TrialLookups/TrialItemTemplateCategories/TrialItemTemplateCategoriesPage").then(m => ({ default: m.default })));
const TrialItemTemplateCategoryPage = React.lazy(() => import("./containers/TrialLookups/TrialItemTemplateCategory/TrialItemTemplateCategoryPage").then(m => ({ default: m.default })));
const TrialItemTemplatePage = React.lazy(() => import("./containers/TrialLookups/TrialItemTemplate/TrialItemTemplatePage").then(m => ({ default: m.default })));
const TrialItemTemplatesPage = React.lazy(() => import("./containers/TrialLookups/TrialItemTemplates/TrialItemTemplatesPage").then(m => ({ default: m.default })));
const TrialItemUpdatePage = React.lazy(() => import("./containers/Trials/TrialItemUpdate/TrialItemUpdatePage").then(m => ({ default: m.default })));
const TrialItemsPage = React.lazy(() => import("./containers/Trials/TrialItems/TrialItemsPage").then(m => ({ default: m.default })));
const TrialLaboratoriesPage = React.lazy(() => import("./containers/Trials/TrialLaboratories/TrialLaboratoriesPage").then(m => ({ default: m.default })));
const TrialNotesPage = React.lazy(() => import("./containers/Trials/TrialNotes/TrialNotesPage").then(m => ({ default: m.default })));
const TrialPage = React.lazy(() => import("./containers/Trials/Trial/TrialPage").then(m => ({ default: m.default })));
const TrialPersonnelPage = React.lazy(() => import("./containers/Trials/TrialPersonnel/TrialPersonnelPage").then(m => ({ default: m.default })));
const TrialPersonnelsPage = React.lazy(() => import("./containers/Trials/TrialPersonnels/TrialPersonnelsPage").then(m => ({ default: m.default })));
const TrialSettingsPage = React.lazy(() => import("./containers/Settings/TrialSettings/TrialSettingsPage").then(m => ({ default: m.default })));
const TrialTypePage = React.lazy(() => import("./containers/TrialLookups/TrialType/TrialTypePage").then(m => ({ default: m.default })));
const TrialTypesPage = React.lazy(() => import("./containers/TrialLookups/TrialTypes/TrialTypesPage").then(m => ({ default: m.default })));
const TrialWebsitePage = React.lazy(() => import("./containers/Trials/TrialWebsite/TrialWebsitePage").then(m => ({ default: m.default })));
const TrialsPage = React.lazy(() => import("./containers/Trials/TrialsPage/TrialsPage").then(m => ({ default: m.default })));

const RouterState = () => {
    const location = useLocation();
    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(UpdateLocation(location));
    }, [location, dispatch]);

    const user = useSelector((state) => state.user.data);

    // Wait for user to load before checking permissions
    if (!user) {
        return <OverlayLoading />;
    }

    if (
        !user.roles?.["Global"]?.includes("BctStaff") &&
        !user.roles?.["Global"]?.includes("Admin") &&
        location.pathname !== "/error/403"
    ) {
        return (
            <>
                <Navigate replace to="/error/403" />
                <OverlayLoading />
            </>
        );
    }

    return <BaseLayout />;
};

export const routes: AppRouteObject[] = [
    {
        path: "/",
        element: (
            <>
                <RouterState />
            </>
        ),
        errorElement: <RouteErrorBoundary />,
        loader: () => ({
            helpPage: null
        }),
        children: [
            {
                index: true,
                element: <Navigate replace to="/dashboard" />,
                loader: () => ({
                    helpPage: null
                }) 
            },
            {
                path: "*",
                element: <Navigate replace to="/error/404" />,
                loader: () => ({
                    helpPage: null
                }) 
            },
            {
                path: "dashboard",
                element: (
                    <React.Suspense fallback={<OverlayLoading />}>
                        <DashboardPage />
                    </React.Suspense>
                ),
                loader: () => ({
                    helpPage: Dtos.Page.Dashboard
                }) 
            },
            {
                path: "search",
                element: (
                    <React.Suspense fallback={<OverlayLoading />}>
                        <SearchPage />
                    </React.Suspense>
                ),
                loader: () => ({
                    helpPage: Dtos.Page.Dashboard
                }) 
            },
            {
                path: "mytrials",
                element: (
                    <React.Suspense fallback={<OverlayLoading />}>
                        <MyTrialsPage />
                    </React.Suspense>
                ),
                loader: () => ({
                    helpPage: Dtos.Page.MyTrials
                }) 
            },
            {
                path: "error",
                loader: () => ({
                    helpPage: null
                }),
                children: [
                    {
                        path: ":errorCode",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <ErrorPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Error
                        }) 
                    },
                ],
            },
            {
                path: "settings",
                loader: () => ({
                    helpPage: null
                }),
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <SettingsPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Settings
                        }) 
                    },
                    {
                        path: "general",
                        loader: () => ({
                            helpPage: null
                        }), 
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <GeneralSettingsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.GeneralSettings
                                }) 
                            },
                            {
                                path: "countries",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CountriesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Countries
                                        }) 
                                    },
                                    {
                                        path: ":countryId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CountryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Country
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "roles",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <RolesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Roles
                                        }) 
                                    },
                                    {
                                        path: ":roleId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <RolePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Role
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "states",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <StatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.States
                                        }) 
                                    },
                                    {
                                        path: ":stateId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <StatePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.State
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "itemcomponenttypes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentTypesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponentTypes
                                        }) 
                                    },
                                    {
                                        path: ":itemcomponenttypesId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentTypePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponentType
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "notetypes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NoteTypesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.NoteTypes
                                        }) 
                                    },
                                    {
                                        path: ":notetypesId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NoteTypePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.NoteType
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "interests",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InterestsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Interests
                                        }) 
                                    },
                                    {
                                        path: ":interestId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InterestPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Interest
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "membershiptypes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <MembershipTypesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.MembershipTypes
                                        }) 
                                    },
                                    {
                                        path: ":membershiptypesId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <MembershipTypePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.MembershipType
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "professionaldisciplines",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <ProfessionalDisciplinesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ProfessionalDisciplines
                                        }) 
                                    },
                                    {
                                        path: ":professionaldisciplinesId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProfessionalDisciplinePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ProfessionalDiscipline
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "salutations",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <SalutationsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Salutations
                                        }) 
                                    },
                                    {
                                        path: ":salutationId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <SalutationPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Salutation
                                        }) 
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "project",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <ProjectSettingsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.ProjectSettings
                                }) 
                            },
                            {
                                path: "itemtemplates",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ProjectItemTemplates
                                        }) 
                                    },
                                    {
                                        path: ":projectItemTemplateId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectItemTemplatePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ProjectItemTemplate
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponents
                                        })
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponent
                                        }),
                                        children: [
                                            {
                                                path: ":cloneId",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <ItemComponentPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.ItemComponent
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemCategories",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <ProjectItemTemplateCategorysPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ProjectItemTemplateCategorys
                                        })
                                    },
                                    {
                                        path: ":projectItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <ProjectItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ProjectItemTemplateCategory
                                        })
                                    },
                                ],
                            },
                            {
                                path: "projectitemimport",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <ProjectItemImportPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.ProjectItemImport
                                })
                            },
                        ],
                    },
                    {
                        path: "trial",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialSettingsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialSettings
                                })
                            },
                            {
                                path: "itemtemplates",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialItemTemplates
                                        })
                                    },
                                    {
                                        path: ":trialId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialItemTemplates
                                                })
                                            },
                                            {
                                                path: "template",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":trialItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialItemTemplate
                                                        })
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                path: ":trialItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialItemTemplate
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponents
                                        })
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponent
                                        }),
                                        children: [
                                            {
                                                path: ":cloneId",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <ItemComponentPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.ItemComponent
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemtemplatecategories",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialItemTemplateCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialItemTemplateCategories
                                        })
                                    },
                                    {
                                        path: ":trialItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialItemTemplateCategory
                                        })
                                    },
                                ],
                            },
                            {
                                path: "trialtypes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialTypesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialTypes
                                        })
                                    },
                                    {
                                        path: ":trialTypeId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialTypePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialType
                                        })
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "trialinstitution",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialInstitutionSettingsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialInstitutionSettings
                                })
                            },
                            {
                                path: "itemtemplates",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialInstitutionItemTemplates
                                        })
                                    },
                                    {
                                        path: ":trialId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialInstitutionItemTemplates
                                                })
                                            },
                                            {
                                                path: "template",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":trialInstitutionItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialInstitutionItemTemplate
                                                        })
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                path: ":trialInstitutionItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialInstitutionItemTemplate
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponents
                                        })
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponent
                                        }),
                                        children: [
                                            {
                                                path: ":cloneId",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <ItemComponentPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.ItemComponent
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemtemplatecategories",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemTemplateCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialInstitutionItemTemplateCategories
                                        })
                                    },
                                    {
                                        path: ":trialInstitutionItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialInstitutionItemTemplateCategory
                                        })
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "trialgroup",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialGroupSettingsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialGroupSettings
                                })
                            },
                            {
                                path: "itemtemplates",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialGroupItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialGroupItemTemplates
                                        })
                                    },
                                    {
                                        path: ":groupId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialGroupItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialGroupItemTemplates
                                                })
                                            },
                                            {
                                                path: "template",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":trialGroupItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialGroupItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialGroupItemTemplate
                                                        })
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                path: ":trialGroupItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialGroupItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialGroupItemTemplate
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponents
                                        })
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponent
                                        }),
                                        children: [
                                            {
                                                path: ":cloneId",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <ItemComponentPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.ItemComponent
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemtemplatecategories",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialGroupItemTemplateCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialGroupItemTemplateCategories
                                        })
                                    },
                                    {
                                        path: ":trialGroupItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialGroupItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialGroupItemTemplateCategory
                                        })
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "group",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <GroupSettingsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.GroupSettings
                                }),
                            },
                            {
                                path: "itemtemplates",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupItemTemplates
                                        })
                                    },
                                    {
                                        path: ":groupId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <GroupItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.GroupItemTemplates
                                                })
                                            },
                                            {
                                                path: "template",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":groupItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <GroupItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.GroupItemTemplate
                                                        })
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                path: ":groupItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <GroupItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.GroupItemTemplate
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponents
                                        })
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponent
                                        }),
                                        children: [
                                            {
                                                path: ":cloneId",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <ItemComponentPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.ItemComponent
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemCategories",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <GroupItemTemplateCategorysPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupItemTemplateCategorys
                                        })
                                    },
                                    {
                                        path: ":groupItemTemplateCategoryId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <GroupItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupItemTemplateCategory
                                        })
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "institution",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionSettingsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.InstitutionSettings
                                })
                            },
                            {
                                path: "itemtemplates",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <InstitutionItemTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionItemTemplates
                                        })
                                    },
                                    {
                                        path: ":institutionId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionItemTemplatesPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.InstitutionItemTemplates
                                                })
                                            },
                                            {
                                                path: "template",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":institutionItemTemplateId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <InstitutionItemTemplatePage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.InstitutionItemTemplate
                                                        })
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "template",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                path: ":institutionItemTemplateId",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionItemTemplatePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.InstitutionItemTemplate
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemcomponents",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponents
                                        })
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponent
                                        }),
                                        children: [
                                            {
                                                path: ":cloneId",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <ItemComponentPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.ItemComponent
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "itemtemplatecategories",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <InstitutionItemTemplateCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionItemTemplateCategories
                                        })
                                    },
                                    {
                                        path: ":institutionItemTemplateId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <InstitutionItemTemplateCategoryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionItemTemplateCategory
                                        })
                                    },
                                ],
                            },
                            {
                                path: "sitelocations",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <SiteLocationsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.SiteLocations
                                        })
                                    },
                                    {
                                        path: ":siteLocationId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <SiteLocationPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.SiteLocation
                                        })
                                    },
                                ],
                            },
                            {
                                path: "keycontacts",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <KeyContactsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.KeyContacts
                                        })
                                    },
                                    {
                                        path: ":keyContactId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <KeyContactPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.KeyContact
                                        })
                                    },
                                ],
                            },
                            {
                                path: "facilityalliancemembers",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <FacilityAllianceMembersPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.FacilityAllianceMembers
                                        })
                                    },
                                    {
                                        path: ":facilityAllianceMemberId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FacilityAllianceMemberPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.FacilityAllianceMember
                                        })
                                    },
                                ],
                            },
                            {
                                path: "institutiontypes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionTypesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionTypes
                                        })
                                    },
                                    {
                                        path: ":institutionTypeId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionTypePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionType
                                        })
                                    },
                                ],
                            },
                            {
                                path: "institutioncodings",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionCodingsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionCodings
                                        })
                                    },
                                    {
                                        path: ":institutionCodingId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionCodingPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionCoding
                                        })
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "qa",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <QaSettingsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.QaSettings
                                })
                            },
                            {
                                path: "formtemplates",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FormTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.FormTemplates
                                        })
                                    },
                                    {
                                        path: ":formTemplateId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FormTemplatePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.FormTemplate
                                        })
                                    },
                                ],
                            },
                            {
                                path: "formcomponents",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponents
                                        })
                                    },
                                    {
                                        path: ":itemComponentId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ItemComponentPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ItemComponent
                                        }),
                                        children: [
                                            {
                                                path: ":cloneId",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <ItemComponentPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.ItemComponent
                                                })
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "reportingtags",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ReportingTagsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ReportingTags
                                        })
                                    },
                                    {
                                        path: ":reportingTagId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ReportingTagPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ReportingTag
                                        })
                                    },
                                ],
                            },
                            {
                                path: "reportingtagcategories",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ReportingTagCategoriesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ReportingTagCategories
                                        })
                                    },
                                    {
                                        path: ":reportingTagCategoryId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ReportingTagCategoryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ReportingTagCategory
                                        })
                                    },
                                ],
                            },
                            {
                                path: "tasktypes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TaskTypesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TaskTypes
                                        })
                                    },
                                    {
                                        path: ":taskTypeId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TaskTypePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TaskType
                                        })
                                    },
                                ],
                            },
                            {
                                path: "trackertypes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrackerTypesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrackerTypes
                                        })
                                    },
                                    {
                                        path: ":trackerTypeId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrackerTypePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrackerType
                                        })
                                    },
                                ],
                            },
                            {
                                path: "trackermilestones",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrackerMilestonesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrackerMilestones
                                        })
                                    },
                                    {
                                        path: ":trackerMilestoneId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrackerMilestonePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrackerMilestone
                                        })
                                    },
                                ],
                            },
                            {
                                path: "formtemplatefiletypes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FormTemplateFileTypesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.FormTemplateFileTypes
                                        })
                                    },
                                    {
                                        path: ":formTemplateFileTypeId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FormTemplateFileTypePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.FormTemplateFileType
                                        })
                                    },
                                ],
                            },
                            {
                                path: "qaemailtemplates",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <QAEmailTemplatesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.QAEmailTemplates
                                        })
                                    },
                                    {
                                        path: ":standardEmailTemplateId",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <QAEmailTemplatePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.QAEmailTemplate
                                        })
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "openclinicauserroles",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <OpenClinicaUserRolesPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.OpenClinicaUserRoles
                                })
                            },
                        ],
                    },
                    {
                        path: "hrecs",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <HrecsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Hrecs
                                })
                            },
                            {
                                path: "create",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <HrecPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Hrec
                                })
                            },
                            {
                                path: ":hrecId",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate replace to="details" />
                                        ),
                                        loader: () => ({
                                            helpPage: null
                                        }) 
                                    },
                                    {
                                        path: "details",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <HrecPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Hrec
                                        })
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "healthdistricts",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <HealthDistrictsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.HealthDistricts
                                })
                            },
                            {
                                path: "create",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <HealthDistrictPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.HealthDistrict
                                })
                            },
                            {
                                path: ":healthDistrictId",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate replace to="details" />
                                        ),
                                        loader: () => ({
                                            helpPage: null
                                        }) 
                                    },
                                    {
                                        path: "details",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <HealthDistrictPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.HealthDistrict
                                        })
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "trials",
                loader: () => ({
                    helpPage: null
                }),
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <TrialsPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Trials
                        })
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <TrialPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Trial
                        })
                    },
                    {
                        path: ":trialId",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                loader: () => ({
                                    helpPage: null
                                }) 
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Trial
                                })
                            },
                            {
                                path: "institutions",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialInstitutionsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialInstitutions
                                        })
                                    },
                                ],
                            },
                            {
                                path: "trialinstitution",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        path: ":trialInstitutionId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialInstitution
                                                })
                                            },
                                            {
                                                path: "items",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionItemsPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialInstitutionItems
                                                })
                                            },
                                            {
                                                path: "personnel",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionPersonnelsPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialInstitutionPersonnels
                                                        })
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionPersonnelPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialInstitutionPersonnel
                                                        })
                                                    },
                                                    {
                                                        path: ":personnelId",
                                                        loader: () => ({
                                                            helpPage: null
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialInstitutionPersonnelPage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialInstitutionPersonnel
                                                                })
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "notes",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionNotesPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialInstitutionNotes
                                                        })
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<OverlayLoading />}>
                                                                <NotePage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.Note
                                                        })
                                                    },
                                                    {
                                                        path: ":noteId",
                                                        loader: () => ({
                                                            helpPage: null
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <NotePage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.Note
                                                                })
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "labs",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionLaboratoriesPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialInstitutionLaboratories
                                                        })
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionLaboratoryPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialInstitutionLaboratory
                                                        })
                                                    },
                                                    {
                                                        path: ":trialInstitutionLaboratoryId",
                                                        loader: () => ({
                                                            helpPage: null
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialInstitutionLaboratoryPage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialInstitutionLaboratory
                                                                })
                                                            },
                                                            {
                                                                path: "personnel",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialInstitutionLaboratoryPersonnelsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            loader: () => ({
                                                                                helpPage: Dtos.Page.TrialInstitutionLaboratoryPersonnels
                                                                            })
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialInstitutionLaboratoryPersonnelPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            loader: () => ({
                                                                                helpPage: Dtos.Page.TrialInstitutionLaboratoryPersonnel
                                                                            })
                                                                    },
                                                                    {
                                                                        path: ":trialInstitutionLaboratoryPersonnelId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }) 
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialInstitutionLaboratoryPersonnelPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                        loader: () => ({
                                                                                            helpPage: Dtos.Page.TrialInstitutionLaboratoryPersonnel
                                                                                        })
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "finance",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialInstitutionLaboratoryFinancePage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialInstitutionLaboratoryFinance
                                                                })
                                                            },
                                                            {
                                                                path: "contracts",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialInstitutionLaboratoryContractsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            loader: () => ({
                                                                                helpPage: Dtos.Page.TrialInstitutionLaboratoryContracts
                                                                            })
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialInstitutionLaboratoryContractPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            loader: () => ({
                                                                                helpPage: Dtos.Page.TrialInstitutionLaboratoryContract
                                                                            })
                                                                    },
                                                                    {
                                                                        path: ":trialInstitutionLaboratoryContractId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }) 
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialInstitutionLaboratoryContractPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                        loader: () => ({
                                                                                            helpPage: Dtos.Page.TrialInstitutionLaboratoryContract
                                                                                        })
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "finance",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <TrialInstitutionFinancePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialInstitutionFinance
                                                })
                                            },
                                            {
                                                path: "contracts",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionContractsPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialInstitutionContracts
                                                        })
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <TrialInstitutionContractPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.TrialInstitutionContract
                                                        })
                                                    },
                                                    {
                                                        path: ":trialInstitutionContractId",
                                                        loader: () => ({
                                                            helpPage: null
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialInstitutionContractPage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialInstitutionContract
                                                                })
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: "monitoring",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <MonitoringsPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.Monitorings
                                                        })
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <MonitoringPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.Monitoring
                                                        })
                                                    },
                                                    {
                                                        path: ":monitoringId",
                                                        loader: () => ({
                                                            helpPage: null
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <MonitoringPage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.Monitoring
                                                                })
                                                            },
                                                            {
                                                                path: "documents",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <MonitoringDocumentsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.MonitoringDocuments
                                                                        })                                                                        
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <MonitoringDocumentPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.MonitoringDocument
                                                                        })
                                                                    },
                                                                    {
                                                                        path: ":monitoringDocumentId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children: [
                                                                            {
                                                                                index: true,
                                                                                element: (
                                                                                    <Navigate replace to="details" />
                                                                                ),
                                                                                loader: () => ({
                                                                                    helpPage: null
                                                                                }) 
                                                                            },
                                                                            {
                                                                                path: "details",
                                                                                element: (
                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                        <MonitoringDocumentPage />
                                                                                    </React.Suspense>
                                                                                ),
                                                                                loader: () => ({
                                                                                    helpPage: Dtos.Page.MonitoringDocument
                                                                                })
                                                                            }
                                                                        ]
                                                                    }
                                                                ]
                                                            },
                                                            {
                                                                path: "issues",
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.Monitorings
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <MonitoringIssuesPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.MonitoringIssues
                                                                        })                                                                    
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <MonitoringIssuePage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.MonitoringIssue
                                                                        })
                                                                    },
                                                                    {
                                                                        path: ":monitoringIssueId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children: [
                                                                            {
                                                                                index: true,
                                                                                element: (
                                                                                    <Navigate replace to="details" />
                                                                                ),
                                                                                loader: () => ({
                                                                                    helpPage: null
                                                                                }) 
                                                                            },
                                                                            {
                                                                                path: "details",
                                                                                element: (
                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                        <MonitoringIssuePage />
                                                                                    </React.Suspense>
                                                                                ),
                                                                                loader: () => ({
                                                                                    helpPage: Dtos.Page.MonitoringIssue
                                                                                })
                                                                            },
                                                                            {
                                                                                path: "documents",
                                                                                loader: () => ({
                                                                                    helpPage: null
                                                                                }),
                                                                                children: [
                                                                                    {
                                                                                        index: true,
                                                                                        element: (
                                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                <MonitoringIssueDocumentsPage />
                                                                                            </React.Suspense>
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: Dtos.Page.MonitoringIssueDocuments
                                                                                        })                                                                       
                                                                                    },
                                                                                    {
                                                                                        path: "create",
                                                                                        element: (
                                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                <MonitoringIssueDocumentPage />
                                                                                            </React.Suspense>
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: Dtos.Page.MonitoringIssueDocument
                                                                                        })
                                                                                    },
                                                                                    {
                                                                                        path: ":monitoringIssueDocumentId",
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }),
                                                                                        children: [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <Navigate replace to="details" />
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: null
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: "details",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <MonitoringIssueDocumentPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.MonitoringIssueDocument
                                                                                                })
                                                                                            }
                                                                                        ]
                                                                                    }
                                                                                ]
                                                                            },
                                                                        ]
                                                                    }
                                                                ]
                                                            }
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "trialinstitutionitemviews",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemCustomViewsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialInstitutionItemCustomViews
                                        }) 
                                    },
                                    {
                                        path: ":trialInstitutionItemCustomViewId/dataentry",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemCustomViewDataEntryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialInstitutionItemCustomViewDataEntry
                                        }) 
                                    },
                                    {
                                        path: ":trialInstitutionItemCustomViewId",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <TrialInstitutionItemCustomViewPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialInstitutionItemCustomView
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "trialitems",
                                loader: () => ({
                                    helpPage: Dtos.Page.Dashboard
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialItemsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialItems
                                        }) 
                                    }
                                ],
                            },
                            {
                                path: "personnel",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialPersonnelsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialPersonnel
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialPersonnelPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialPersonnel
                                        }) 
                                    },
                                    {
                                        path: ":trialPersonnelId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <TrialPersonnelPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialPersonnel
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "accrual",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialAccrualsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialAccruals
                                }) 
                            },
                            {
                                path: "notes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialNotesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialNotes
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NotePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Note
                                        }) 
                                    },
                                    {
                                        path: ":noteId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <NotePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.Note
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "groups",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <TrialGroupsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.TrialGroups
                                        }) 
                                    },
                                    {
                                        path: ":groupId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                path: "trialgroup",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":trialGroupId",
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.Dashboard
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupPage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialGroup
                                                                }) 
                                                            },
                                                            {
                                                                path: "personnel",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupPersonnelsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                            loader: () => ({
                                                                                helpPage: Dtos.Page.TrialGroupPersonnels
                                                                            }) 
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupPersonnelPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupPersonnel
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: ":trialPersonnelId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }) 
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupPersonnelPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    loader: () => ({
                                                                                        helpPage: Dtos.Page.TrialGroupPersonnel
                                                                                    }) 
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "items",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupItemsPage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialGroupItems
                                                                }) 
                                                            },
                                                            {
                                                                path: "labs",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupLaboratoriesPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupLaboratories
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupLaboratoryPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupLaboratory
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: ":trialGroupLaboratoryId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }) 
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupLaboratoryPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    loader: () => ({
                                                                                        helpPage: Dtos.Page.TrialGroupLaboratory
                                                                                    }) 
                                                                                },
                                                                                {
                                                                                    path: "personnel",
                                                                                    loader: () => ({
                                                                                        helpPage: null
                                                                                    }),
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryPersonnelsPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.TrialGroupLaboratoryPersonnels
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: "create",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryPersonnelPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.TrialGroupLaboratoryPersonnel
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: ":trialGroupLaboratoryPersonnelId",
                                                                                                loader: () => ({
                                                                                                    helpPage: null
                                                                                                }),
                                                                                                children:
                                                                                                    [
                                                                                                        {
                                                                                                            index: true,
                                                                                                            element:
                                                                                                                (
                                                                                                                    <Navigate replace to="details" />
                                                                                                                ),
                                                                                                                loader: () => ({
                                                                                                                    helpPage: null
                                                                                                                }) 
                                                                                                        },
                                                                                                        {
                                                                                                            path: "details",
                                                                                                            element: (
                                                                                                                <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                                    <TrialGroupLaboratoryPersonnelPage />
                                                                                                                </React.Suspense>
                                                                                                            ),
                                                                                                            loader: () => ({
                                                                                                                helpPage: Dtos.Page.TrialGroupLaboratoryPersonnel
                                                                                                            }) 
                                                                                                        },
                                                                                                    ],
                                                                                            },
                                                                                        ],
                                                                                },
                                                                                {
                                                                                    path: "finance",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupLaboratoryFinancePage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    loader: () => ({
                                                                                        helpPage: Dtos.Page.TrialGroupLaboratoryFinance
                                                                                    }) 
                                                                                },
                                                                                {
                                                                                    path: "contracts",
                                                                                    loader: () => ({
                                                                                        helpPage: null
                                                                                    }),
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryContractsPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.TrialGroupLaboratoryContracts
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: "create",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryContractPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.TrialGroupLaboratoryContract
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: ":trialGroupLaboratoryContractId",
                                                                                                loader: () => ({
                                                                                                    helpPage: null
                                                                                                }),
                                                                                                children:
                                                                                                    [
                                                                                                        {
                                                                                                            index: true,
                                                                                                            element:
                                                                                                                (
                                                                                                                    <Navigate replace to="details" />
                                                                                                                ),
                                                                                                                loader: () => ({
                                                                                                                    helpPage: null
                                                                                                                }) 
                                                                                                        },
                                                                                                        {
                                                                                                            path: "details",
                                                                                                            element: (
                                                                                                                <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                                    <TrialGroupLaboratoryContractPage />
                                                                                                                </React.Suspense>
                                                                                                            ),
                                                                                                            loader: () => ({
                                                                                                                helpPage: Dtos.Page.TrialGroupLaboratoryContract
                                                                                                            }) 
                                                                                                        },
                                                                                                    ],
                                                                                            },
                                                                                        ],
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "finance",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupFinancePage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialGroupFinance
                                                                }) 
                                                            },
                                                            {
                                                                path: "contracts",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupContractsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupContracts
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupContractPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupContract
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: ":trialGroupContractId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }) 
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupContractPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    loader: () => ({
                                                                                        helpPage: Dtos.Page.TrialGroupContract
                                                                                    }) 
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: ":trialGroupId",
                                        loader: () => ({
                                            helpPage: Dtos.Page.Dashboard
                                        }),
                                        children: [
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <TrialGroupPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.TrialGroup
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "datasharing",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialDoiPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialDoi
                                }) 
                            },
                            {
                                path: "website",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialWebsitePage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialWebsite
                                }) 
                            },
                            {
                                path: "finance",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialFinancePage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialFinance
                                }) 
                            },
                            {
                                path: "trialitemupdate",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialItemUpdatePage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialItemUpdate
                                }) 
                            },
                            {
                                path: "labs",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <TrialLaboratoriesPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.TrialLaboratories
                                }) 
                            },
                        ],
                    },
                    {
                        path: ":projectId",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                path: "project",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        path: "details",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Project
                                        }) 
                                    },
                                    {
                                        path: "investigators",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                path: ":projectInvestigatorId",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <Navigate replace to="details" />
                                                        ),
                                                        loader: () => ({
                                                            helpPage: null
                                                        }) 
                                                    },
                                                    {
                                                        path: "details",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <ProjectInvestigatorPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.ProjectInvestigator
                                                        }) 
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "discussions",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <DiscussionsPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.Discussions
                                                }) 
                                            },
                                            {
                                                path: "create",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <DiscussionPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.Discussion
                                                }) 
                                            },
                                            {
                                                path: ":discussionId",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <Navigate replace to="details" />
                                                        ),
                                                        loader: () => ({
                                                            helpPage: null
                                                        }) 
                                                    },
                                                    {
                                                        path: "details",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <DiscussionPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.Discussion
                                                        }) 
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: "items",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectItemsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ProjectItems
                                        }) 
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "committees",
                loader: () => ({
                    helpPage: null
                }),
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <CommitteesPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Committees
                        }) 
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <CommitteePage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Committee
                        }) 
                    },
                    {
                        path: ":committeeId",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                loader: () => ({
                                    helpPage: null
                                }) 
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <CommitteePage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Committee
                                }) 
                            },
                            {
                                path: "meetings",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CommitteeMeetingsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.CommitteeMeetings
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CommitteeMeetingPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.CommitteeMeeting
                                        }) 
                                    },
                                    {
                                        path: ":committeeMeetingId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <CommitteeMeetingPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.CommitteeMeeting
                                                }) 
                                            },
                                            {
                                                path: "attendances",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <CommitteeMeetingAttendancesPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.CommitteeMeetingAttendances
                                                        }) 
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <CommitteeMeetingAttendancePage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.CommitteeMeetingAttendance
                                                        }) 
                                                    },
                                                    {
                                                        path: ":committeeMeetingAttendanceId",
                                                        loader: () => ({
                                                            helpPage: null
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <CommitteeMeetingAttendancePage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.CommitteeMeetingAttendance
                                                                }) 
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "members",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CommitteeMembersPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.CommitteeMembers
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <CommitteeMemberPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.CommitteeMember
                                        }) 
                                    },
                                    {
                                        path: ":committeeMemberId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <CommitteeMemberPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.CommitteeMember
                                                }) 
                                            },
                                            {
                                                path: "roles",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <CommitteeMemberRolesPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.CommitteeMemberRoles
                                                        }) 
                                                    },
                                                    {
                                                        path: "create",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <CommitteeMemberRolePage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.CommitteeMemberRole
                                                        }) 
                                                    },
                                                    {
                                                        path: ":committeeMemberRoleId",
                                                        loader: () => ({
                                                            helpPage: null
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <CommitteeMemberRolePage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.CommitteeMemberRole
                                                                }) 
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "regularinvitees",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <CommitteeRegularInviteesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.CommitteeRegularInvitees
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <CommitteeRegularInviteePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.CommitteeRegularInvitee
                                        }) 
                                    },
                                    {
                                        path: ":committeeRegularInviteeId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <CommitteeRegularInviteePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.CommitteeRegularInvitee
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "groups",
                loader: () => ({
                    helpPage: null
                }),
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <GroupsPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Groups
                        }) 
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <GroupPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Group
                        }) 
                    },
                    {
                        path: ":groupId",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                loader: () => ({
                                    helpPage: null
                                }) 
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <GroupPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Group
                                }) 
                            },
                            {
                                path: "trials",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupTrialsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupTrials
                                        }) 
                                    },
                                    {
                                        path: ":trialId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                path: "trialgroup",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":trialGroupId",
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.Dashboard
                                                        }),
                                                        children: [
                                                            {
                                                                index: true,
                                                                element: (
                                                                    <Navigate replace to="details" />
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }) 
                                                            },
                                                            {
                                                                path: "details",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupPage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialGroup
                                                                }) 
                                                            },
                                                            {
                                                                path: "personnel",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupPersonnelsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupPersonnels
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupPersonnelPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupPersonnel
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: ":trialPersonnelId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }) 
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupPersonnelPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    loader: () => ({
                                                                                        helpPage: Dtos.Page.TrialGroupPersonnel
                                                                                    }) 
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "items",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupItemsPage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialGroupItems
                                                                }) 
                                                            },
                                                            {
                                                                path: "labs",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupLaboratoriesPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupLaboratories
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupLaboratoryPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupLaboratory
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: ":trialGroupLaboratoryId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }) 
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupLaboratoryPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    loader: () => ({
                                                                                        helpPage: Dtos.Page.TrialGroupLaboratory
                                                                                    }) 
                                                                                },
                                                                                {
                                                                                    path: "personnel",
                                                                                    loader: () => ({
                                                                                        helpPage: null
                                                                                    }),
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryPersonnelsPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.TrialGroupLaboratoryPersonnels
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: "create",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryPersonnelPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.TrialGroupLaboratoryPersonnel
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: ":trialGroupLaboratoryPersonnelId",
                                                                                                loader: () => ({
                                                                                                    helpPage: null
                                                                                                }),
                                                                                                children:
                                                                                                    [
                                                                                                        {
                                                                                                            index: true,
                                                                                                            element:
                                                                                                                (
                                                                                                                    <Navigate replace to="details" />
                                                                                                                ),
                                                                                                                loader: () => ({
                                                                                                                    helpPage: null
                                                                                                                }) 
                                                                                                        },
                                                                                                        {
                                                                                                            path: "details",
                                                                                                            element: (
                                                                                                                <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                                    <TrialGroupLaboratoryPersonnelPage />
                                                                                                                </React.Suspense>
                                                                                                            ),
                                                                                                            loader: () => ({
                                                                                                                helpPage: Dtos.Page.TrialGroupLaboratoryPersonnel
                                                                                                            }) 
                                                                                                        },
                                                                                                    ],
                                                                                            },
                                                                                        ],
                                                                                },
                                                                                {
                                                                                    path: "finance",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupLaboratoryFinancePage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    loader: () => ({
                                                                                        helpPage: Dtos.Page.TrialGroupLaboratoryFinance
                                                                                    }) 
                                                                                },
                                                                                {
                                                                                    path: "contracts",
                                                                                    loader: () => ({
                                                                                        helpPage: null
                                                                                    }),
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryContractsPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.TrialGroupLaboratoryContracts
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: "create",
                                                                                                element: (
                                                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                        <TrialGroupLaboratoryContractPage />
                                                                                                    </React.Suspense>
                                                                                                ),
                                                                                                loader: () => ({
                                                                                                    helpPage: Dtos.Page.TrialGroupLaboratoryContract
                                                                                                }) 
                                                                                            },
                                                                                            {
                                                                                                path: ":trialGroupLaboratoryContractId",
                                                                                                loader: () => ({
                                                                                                    helpPage: null
                                                                                                }),
                                                                                                children:
                                                                                                    [
                                                                                                        {
                                                                                                            index: true,
                                                                                                            element:
                                                                                                                (
                                                                                                                    <Navigate replace to="details" />
                                                                                                                ),
                                                                                                                loader: () => ({
                                                                                                                    helpPage: null
                                                                                                                }) 
                                                                                                        },
                                                                                                        {
                                                                                                            path: "details",
                                                                                                            element: (
                                                                                                                <React.Suspense fallback={<div>Loading...</div>}>
                                                                                                                    <TrialGroupLaboratoryContractPage />
                                                                                                                </React.Suspense>
                                                                                                            ),
                                                                                                            loader: () => ({
                                                                                                                helpPage: Dtos.Page.TrialGroupLaboratoryContract
                                                                                                            }) 
                                                                                                        },
                                                                                                    ],
                                                                                            },
                                                                                        ],
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                            {
                                                                path: "finance",
                                                                element: (
                                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                                        <TrialGroupFinancePage />
                                                                    </React.Suspense>
                                                                ),
                                                                loader: () => ({
                                                                    helpPage: Dtos.Page.TrialGroupFinance
                                                                }) 
                                                            },
                                                            {
                                                                path: "contracts",
                                                                loader: () => ({
                                                                    helpPage: null
                                                                }),
                                                                children: [
                                                                    {
                                                                        index: true,
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupContractsPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupContracts
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: "create",
                                                                        element: (
                                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                                <TrialGroupContractPage />
                                                                            </React.Suspense>
                                                                        ),
                                                                        loader: () => ({
                                                                            helpPage: Dtos.Page.TrialGroupContract
                                                                        }) 
                                                                    },
                                                                    {
                                                                        path: ":trialGroupContractId",
                                                                        loader: () => ({
                                                                            helpPage: null
                                                                        }),
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    element:
                                                                                        (
                                                                                            <Navigate replace to="details" />
                                                                                        ),
                                                                                        loader: () => ({
                                                                                            helpPage: null
                                                                                        }) 
                                                                                },
                                                                                {
                                                                                    path: "details",
                                                                                    element: (
                                                                                        <React.Suspense fallback={<div>Loading...</div>}>
                                                                                            <TrialGroupContractPage />
                                                                                        </React.Suspense>
                                                                                    ),
                                                                                    loader: () => ({
                                                                                        helpPage: Dtos.Page.TrialGroupContract
                                                                                    }) 
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "notes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupNotesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupNotes
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NotePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Note
                                        }) 
                                    },
                                    {
                                        path: ":noteId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <NotePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.Note
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "personnel",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupPersonnelsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupPersonnels
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupPersonnelPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupPersonnel
                                        }) 
                                    },
                                    {
                                        path: ":trialPersonnelId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <GroupPersonnelPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.GroupPersonnel
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "contracts",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupContractPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupContract
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "finances",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupFinancePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupFinance
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "items",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <GroupItemsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.GroupItems
                                        }) 
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "personnel",
                loader: () => ({
                    helpPage: null
                }), 
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <PersonnelsPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Personnels
                        }) 
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <PersonnelPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Personnel
                        }) 
                    },
                    {
                        path: "byroles",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <PersonnelByRolesPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.PersonnelByRoles
                        }) 
                    },
                    {
                        path: ":personnelId",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                loader: () => ({
                                    helpPage: null
                                }) 
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Personnel
                                }) 
                            },
                            {
                                path: "trainingcertifications",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelTrainingCertificationPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.PersonnelTrainingCertification
                                }) 
                            },
                            {
                                path: "roles",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <PersonnelRolesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.PersonnelRoles
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <PersonnelRolePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.PersonnelRole
                                        }) 
                                    },
                                    {
                                        path: ":personnelRoleId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <PersonnelRolePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.PersonnelRole
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "permissions",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelPermissionsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.PersonnelPermissions
                                }) 
                            },
                            {
                                path: "openclinica",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelOCRolesPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.PersonnelOCRoles
                                }) 
                            },
                            {
                                path: "activedirectory",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <PersonnelActiveDirectoryPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.PersonnelActiveDirectory
                                }) 
                            },
                            {
                                path: "notes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <PersonnelNotesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.PersonnelNotes
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NotePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Note
                                        }) 
                                    },
                                    {
                                        path: ":noteId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <NotePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.Note
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "fellowship",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FellowshipsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Fellowships
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <FellowshipPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Fellowship
                                        }) 
                                    },
                                    {
                                        path: ":fellowshipId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <FellowshipPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.Fellowship
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "projects",
                loader: () => ({
                    helpPage: null
                }),
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <ProjectsPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Projects
                        }) 
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <ProjectPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Project
                        }) 
                    },
                    {
                        path: ":projectId",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                loader: () => ({
                                    helpPage: null
                                }) 
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <ProjectPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Project
                                }) 
                            },
                            {
                                path: "items",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <ProjectItemsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.ProjectItems
                                }) 
                            },
                            {
                                path: "investigators",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <ProjectInvestigatorPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.ProjectInvestigator
                                        }) 
                                    },
                                    {
                                        path: ":projectInvestigatorId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <ProjectInvestigatorPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.ProjectInvestigator
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "discussions",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <DiscussionsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Discussions
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <DiscussionPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Discussion
                                        }) 
                                    },
                                    {
                                        path: ":discussionId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <DiscussionPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.Discussion
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: "institutions",
                loader: () => ({
                    helpPage: null
                }),
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <InstitutionsPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Institutions
                        }) 
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <InstitutionPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Institution
                        }) 
                    },
                    {
                        path: ":institutionId",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                loader: () => ({
                                    helpPage: null
                                }) 
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Institution
                                }) 
                            },
                            {
                                path: "departments",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionDepartmentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionDepartments
                                        }) 
                                    },
                                    {
                                        path: ":departmentId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionDepartmentEditPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.InstitutionDepartmentEdit
                                                }) 
                                            },
                                            {
                                                path: "contacts",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":departmentContactId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <InstitutionDepartmentContactEditPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.InstitutionDepartmentContactEdit
                                                        }) 
                                                    },
                                                ],
                                            },
                                            {
                                                path: "personnel",
                                                loader: () => ({
                                                    helpPage: null
                                                }),
                                                children: [
                                                    {
                                                        path: ":personnelRoleId",
                                                        element: (
                                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                                <InstitutionDepartmentPersonnelEditPage />
                                                            </React.Suspense>
                                                        ),
                                                        loader: () => ({
                                                            helpPage: Dtos.Page.InstitutionDepartmentPersonnelEdit
                                                        }) 
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "institutionitems",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionItemsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionItems
                                        }) 
                                    },
                                ],
                            },
                            {
                                path: "personnel",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionPersonnelsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionPersonnels
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionPersonnelPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionPersonnel
                                        }) 
                                    },
                                    {
                                        path: ":trialPersonnelId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionPersonnelPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.InstitutionPersonnel
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "labs",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <InstitutionLaboratoriesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionLaboratories
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionLaboratoryPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionLaboratory
                                        }) 
                                    },
                                    {
                                        path: ":institutionLaboratoryId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionLaboratoryPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.InstitutionLaboratory
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "notes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionNotesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionNotes
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <NotePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.Note
                                        }) 
                                    },
                                    {
                                        path: ":noteId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <NotePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.Note
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "procedures",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionProceduresPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionProcedures
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionProcedurePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionProcedure
                                        }) 
                                    },
                                    {
                                        path: ":institutionProcedureId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<div>Loading...</div>}>
                                                        <InstitutionProcedurePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.InstitutionProcedure
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "finance",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionFinancePage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.InstitutionFinance
                                }) 
                            },
                            {
                                path: "contract",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionContractPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.InstitutionContract
                                }) 
                            },
                            {
                                path: "codes",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionCodesPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionCodes
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <InstitutionCodePage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.InstitutionCode
                                        }) 
                                    },
                                    {
                                        path: ":institutionCodeId",
                                        loader: () => ({
                                            helpPage: Dtos.Page.Dashboard
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: <Navigate replace to="details" />,
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <InstitutionCodePage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.InstitutionCode
                                                }) 
                                            }
                                        ],
                                    },
                                ]
                            },
                            {
                                path: "trials",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <InstitutionTrialsPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.InstitutionTrials
                                }) 
                            }
                        ],
                    },
                ],
            },
            {
                path: "labs",
                loader: () => ({
                    helpPage: null
                }),
                children: [
                    {
                        index: true,
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <LabsPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Labs
                        }) 
                    },
                    {
                        path: "create",
                        element: (
                            <React.Suspense fallback={<OverlayLoading />}>
                                <LabPage />
                            </React.Suspense>
                        ),
                        loader: () => ({
                            helpPage: Dtos.Page.Lab
                        }) 
                    },
                    {
                        path: ":laboratoryId",
                        loader: () => ({
                            helpPage: null
                        }),
                        children: [
                            {
                                index: true,
                                element: <Navigate replace to="details" />,
                                loader: () => ({
                                    helpPage: null
                                }) 
                            },
                            {
                                path: "details",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <LabPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.Lab
                                }) 
                            },
                            {
                                path: "departments",
                                loader: () => ({
                                    helpPage: Dtos.Page.Dashboard
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabDepartmentsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.LabDepartments
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabDepartmentPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.LabDepartment
                                        }) 
                                    },
                                    {
                                        path: ":laboratoryDepartmentId",
                                        loader: () => ({
                                            helpPage: null
                                        }), 
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <LabDepartmentPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.LabDepartment
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "personnel",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabPersonnelsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.LabPersonnels
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabPersonnelPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.LabPersonnel
                                        }) 
                                    },
                                    {
                                        path: ":laboratoryPersonnelId",
                                        loader: () => ({
                                            helpPage: null
                                        }), 
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <LabPersonnelPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.LabPersonnel
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "institutions",
                                loader: () => ({
                                    helpPage: null
                                }),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabInstitutionsPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.LabInstitutions
                                        }) 
                                    },
                                    {
                                        path: "create",
                                        element: (
                                            <React.Suspense fallback={<OverlayLoading />}>
                                                <LabInstitutionPage />
                                            </React.Suspense>
                                        ),
                                        loader: () => ({
                                            helpPage: Dtos.Page.LabInstitution
                                        }) 
                                    },
                                    {
                                        path: ":institutionLaboratoryId",
                                        loader: () => ({
                                            helpPage: null
                                        }),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate replace to="details" />
                                                ),
                                                loader: () => ({
                                                    helpPage: null
                                                }) 
                                            },
                                            {
                                                path: "details",
                                                element: (
                                                    <React.Suspense fallback={<OverlayLoading />}>
                                                        <LabInstitutionPage />
                                                    </React.Suspense>
                                                ),
                                                loader: () => ({
                                                    helpPage: Dtos.Page.LabInstitutions
                                                }) 
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "finance",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <LabFinancePage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.LabFinance
                                }) 
                            },
                            {
                                path: "contract",
                                element: (
                                    <React.Suspense fallback={<OverlayLoading />}>
                                        <LabContractPage />
                                    </React.Suspense>
                                ),
                                loader: () => ({
                                    helpPage: Dtos.Page.LabContract
                                }) 
                            },
                        ],
                    },
                ],
            },
        ],
    },

];